import { IconCalendar, IconPlus, IconRepeat } from '@tabler/icons-react'
import { useState } from 'react'

import { AstraButton, AstraDialog, AstraDialogProps, AstraIcon } from '~/astra'
import { DialogBreadcrumb, DialogBreadcrumbsHeader, Show } from '~/components'
import { useStore } from '~/store/store'
import { useActiveRecurringAssignments } from '~/storm'

import { ApplyPreviewDialog } from './Apply/ApplyPreviewDialog'
import { RecurringAssignmentCreation } from './Creation/RecurringAssignmentCreation'
import { RecurringAssignmentList } from './RecurringAssignmentList'

type Props = {
    onClose: () => void
}

const ALL_ASSIGNMENTS_TITLE = 'Alle regler'
const NEW_ASSIGNMENT_TITLE = 'Ny regel'
const PREVIEW_PLAN_TITLE = 'Forhåndsvis plan'

type ModalView = 'list' | 'creation' | 'preview'
export const RecurringAssignmentsModal = ({ onClose }: Props) => {
    const [modalView, setModalView] = useState<ModalView>('list')
    const activeRecurringAssignments = useActiveRecurringAssignments()

    const clearRecurringAssignmentApplyPreviewData = useStore(state => state.surgeonScheduler.actions.clearRecurringAssignmentApplyPreviewData)

    const navigateToList = () => setModalView('list')

    const viewProps = {
        list: {
            title: ALL_ASSIGNMENTS_TITLE,
            header: <DialogBreadcrumb title={ALL_ASSIGNMENTS_TITLE} icon={<AstraIcon icon={IconRepeat} />} />,
            footer: (
                <div className="flex w-full justify-end gap-2">
                    <AstraButton label="Ny regel" icon={<AstraIcon icon={IconPlus} />} variant="secondary" onClick={() => setModalView('creation')} />
                    <AstraButton label={PREVIEW_PLAN_TITLE} icon={<AstraIcon icon={IconCalendar} />} onClick={() => setModalView('preview')} />
                </div>
            ),
        },
        creation: {
            title: NEW_ASSIGNMENT_TITLE,
            disableOutsideClick: true,
            header: (
                <DialogBreadcrumbsHeader
                    onBackClick={navigateToList}
                    crumbs={[
                        {
                            title: ALL_ASSIGNMENTS_TITLE,
                            onClick: navigateToList,
                        },
                        { title: NEW_ASSIGNMENT_TITLE, icon: <AstraIcon icon={IconRepeat} /> },
                    ]}
                />
            ),
        },
        preview: {
            title: PREVIEW_PLAN_TITLE,
            disableOutsideClick: true,
            header: (
                <DialogBreadcrumbsHeader
                    onBackClick={navigateToList}
                    crumbs={[
                        {
                            title: ALL_ASSIGNMENTS_TITLE,
                            onClick: () => {
                                clearRecurringAssignmentApplyPreviewData()
                                navigateToList()
                            },
                        },
                        { title: 'Forhåndsvis plan', icon: <AstraIcon icon={IconCalendar} /> },
                    ]}
                />
            ),
        },
    } satisfies Record<ModalView, Partial<AstraDialogProps>>

    // Maximizing preview dialog height
    const dialogClassName = modalView === 'preview' ? 'top-[5vh]' : ''

    function onClosePreviewDialog() {
        clearRecurringAssignmentApplyPreviewData()
        onClose()
    }

    return (
        <AstraDialog
            align="top"
            isOpen
            onCloseDialog={modalView === 'preview' ? onClosePreviewDialog : onClose}
            {...viewProps[modalView]}
            className={dialogClassName}
        >
            <Show condition={modalView === 'list'}>
                <RecurringAssignmentList recurringAssignments={activeRecurringAssignments} />
            </Show>
            <Show condition={modalView === 'creation'}>
                <RecurringAssignmentCreation onBackClick={() => setModalView('list')} />
            </Show>
            <Show condition={modalView === 'preview'}>
                <ApplyPreviewDialog onClose={onClosePreviewDialog} />
            </Show>
        </AstraDialog>
    )
}
